'use client';
import { ClinicSearchResult, getAlgoliaSearchResponse } from '@/api/algolia';
import { MayBeSearchLocation, getDeliveryModeFilterFromQueryParameters, makeSearchParams } from '@/utils/search';
import { capitalizeWordsInString, getEnumValueFromString, getStringFromQueryParam } from '@/utils/string';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ReadonlyURLSearchParams, useSearchParams } from 'next/navigation';
import Paginator from '@/app/_components/paginator';
import Container from '@/app/_components/container';
import { Bars4Icon, MapPinIcon } from '@heroicons/react/24/outline';
import { trackLicensedItemInSearchList } from '@/utils/trackingGtm';
import ListingFilters from './listingFilters';
import { ListingInfo } from './types';
import { convertSearchResponseIntoListingInfo } from '@/utils/listing';
import { makeMarkersList } from '@/utils/maps';
import { ELButton, ELLoader } from '@davincihealthcare/elty-design-system-react';
import clsx from 'clsx/lite';
import Image from 'next/image';
import { DAVINCI_CDN } from '@/costants/defaultValues';
import { ListingOrder } from '@/app/_components/widgets/listing/listingOrder';
import { useListingOrder } from '@/app/_hooks/useListingOrder';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';
import { useIsDesktop } from '@/app/_hooks/useIsScreen';
import { getUserLocation } from '@/api/mapbox';
import Searchbar from '@/app/_components/searchbar';
import { BannerWelfare } from '@/app/_components/widgets/listing/BannerWelfare';
import { useHasScrolledOnce } from '@/app/_hooks/useHasScrolledOnce';
import CardV3 from '@/app/_components/widgets/listing/cardV3';
import { formatPrice } from '@/utils/checkout';
import { formatDate } from '@/utils/dates';
import MapStaticToInteractive from '@/app/_components/widgets/map';
import { ChannelFilterType, DateFilterType, PaymentFilterType, SortingListingType } from '@/api/algolia/types';

type ListingProps = {
  service?: string;
  locationInfo?: MayBeSearchLocation;
  searchResultsServerSide?: ClinicSearchResult & { date: string };
  paginationPadding?: 'lg' | 'md';
  filterLight?: boolean;
  setLoadingState?: (isLoading: boolean) => void;
  loadingState?: boolean;
  showSearchNearYouCTA?: boolean;
  showSearchSection?: boolean;
  loadMapOnScroll?: boolean;
};

export const getGeoLocationParams = (
  params: ReadonlyURLSearchParams | null,
  { place, coordinates }: { place: string; coordinates: { lat: number; lng: number } },
) => {
  const newParams = new URLSearchParams(params?.toString());
  newParams.delete('page');
  newParams.set('location', place);
  newParams.set('lat', coordinates.lat?.toString());
  newParams.set('lng', coordinates.lng?.toString());
  newParams.set('userGeolocationActive', 'true');

  return newParams;
};

const Listing = ({
  service,
  locationInfo,
  searchResultsServerSide,
  paginationPadding = 'lg',
  filterLight,
  setLoadingState,
  loadingState,
  showSearchNearYouCTA = false,
  showSearchSection = false,
  loadMapOnScroll = false,
}: ListingProps) => {
  const isDesktopAndBigger = useIsDesktop({ andSmaller: false });
  const params = useSearchParams();
  const isWelfare = useIsWelfare();

  const isServerListing = useMemo(() => {
    return !!(searchResultsServerSide?.nbHits && !params?.size && !isWelfare);
  }, [isWelfare, params?.size, searchResultsServerSide?.nbHits]);

  const [listingInfo, setListingInfo] = useState<ListingInfo>(
    !!searchResultsServerSide?.nbHits ? convertSearchResponseIntoListingInfo(service, locationInfo?.name, searchResultsServerSide) : {},
  );

  const [location, setLocation] = useState<MayBeSearchLocation | undefined>(locationInfo);

  const [isShowingMap, setShowMap] = useState(false);
  const [hasMapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (!hasMapLoaded && isShowingMap) {
      setMapLoaded(true);
    }
  }, [hasMapLoaded, isShowingMap]);

  const [isShowingSearchNearYou, setIsShowingSearchNearYou] = useState(showSearchNearYouCTA);

  const locationQueryParam = params?.get('location') ?? '';
  const serviceSearchParam = params?.get('service') ?? '';

  const hasLocation = !!(location || locationQueryParam);

  const { orderValuesLoading, setOrderLoading, setOrderValue, resetOrderValues } = useListingOrder();

  const [isSearchNearYouLoading, setIsSearchNearYouLoading] = useState(false);

  const searchNearYou = async () => {
    setIsSearchNearYouLoading(true);
    try {
      const currentLocation = await getUserLocation();
      if (!currentLocation) {
        setIsSearchNearYouLoading(false);
        return;
      }
      setLocation({
        latitude: currentLocation?.userCoordinates?.[0] ? `${currentLocation?.userCoordinates?.[0]}` : '',
        longitude: currentLocation?.userCoordinates?.[1] ? `${currentLocation?.userCoordinates?.[1]}` : '',
        name: currentLocation?.userLocationName,
      });
      scrollToListingStart();
      setIsSearchNearYouLoading(false);
      setIsShowingSearchNearYou(false);
    } catch (error) {
      console.error('Error checking location permission:', error);
      setIsSearchNearYouLoading(false);
      return null;
    }
  };

  const serverDateFormatted = useMemo(() => {
    if (!searchResultsServerSide?.date) return '';
    const date = new Date(searchResultsServerSide.date);
    // if Date is not valid
    if (isNaN(date.getTime())) return '';
    return capitalizeWordsInString(
      formatDate({
        date,
        localeOptions: { year: 'numeric', day: '2-digit', month: 'long' },
      }),
    );
  }, [searchResultsServerSide?.date]);

  const hasScrolled = useHasScrolledOnce();
  const shouldLoadDesktopMap = loadMapOnScroll ? hasScrolled : true;

  const userGeolocationActive = params?.get('userGeolocationActive');
  const isUserGeolocationActive = getStringFromQueryParam(userGeolocationActive ?? undefined) === 'true' ? true : false;

  useEffect(() => {
    // Use server side results when possible
    if (isServerListing) {
      return;
    }

    let shouldIgnore = false;

    const doNewSearch = async () => {
      const parsedPage = Number(params?.get('page') ?? '1');
      const currentPage = isNaN(parsedPage) ? 1 : parsedPage;

      const queryDeliveryFilter = getDeliveryModeFilterFromQueryParameters(params?.get('exclude') ?? '');
      const queryOrderFilter = getEnumValueFromString(SortingListingType, getStringFromQueryParam(params?.get('order') ?? ''));

      if (queryOrderFilter === SortingListingType.priceAsc) {
        setOrderLoading(SortingListingType.priceAsc);
      }
      if (queryOrderFilter === SortingListingType.firstAvailabilityAsc) {
        setOrderLoading(SortingListingType.firstAvailabilityAsc);
      }

      if (queryOrderFilter === SortingListingType.distanceAsc) {
        setOrderLoading(SortingListingType.distanceAsc);
      }

      const channelFilter = params?.get('channelFilter')?.split(',');
      const paymentFilter = getStringFromQueryParam(params?.get('paymentFilter') ?? '');
      const dateFilter = getStringFromQueryParam(params?.get('dateFilter') ?? '');
      const { searchHealthcareService, searchLocation, searchPage, searchFilter, searchOrder } = await makeSearchParams(
        {
          service: serviceSearchParam,
          lat: params?.get('lat') ?? '',
          lng: params?.get('lng') ?? '',
          page: currentPage,
          location: locationQueryParam,
        },
        service,
        location,
        {
          excludeDomicilary: queryDeliveryFilter?.excludeDomicilary,
          excludeOnline: queryDeliveryFilter?.excludeOnline,
          excludeOnsite: queryDeliveryFilter?.excludeOnsite,
          dateFilter: dateFilter as DateFilterType,
          channel: channelFilter as ChannelFilterType[],
          paymentType: isWelfare ? PaymentFilterType.online : (paymentFilter as PaymentFilterType),
          excludePrice0: isWelfare,
        },
        queryOrderFilter,
      );

      const searchResponse = await getAlgoliaSearchResponse({
        text: searchHealthcareService,
        searchLocation,
        page: searchPage,
        filterObj: searchFilter,
        order: searchOrder,
      });

      const newListingInfo = convertSearchResponseIntoListingInfo(searchHealthcareService, searchLocation?.name, searchResponse);
      // With this we make sure only the last search is applied
      if (!shouldIgnore) {
        setListingInfo(newListingInfo);
        trackLicensedItemInSearchList(newListingInfo);
        setLoadingState?.(false);

        if (isUserGeolocationActive && (newListingInfo.total ?? 0) <= 0) {
          setShowMap(false);
        }
        if (queryOrderFilter === SortingListingType.distanceAsc && isUserGeolocationActive) {
          setOrderValue(SortingListingType.distanceAsc, searchResponse);
        }
        if (queryOrderFilter === SortingListingType.priceAsc) {
          setOrderValue(SortingListingType.priceAsc, searchResponse);
        }
        if (queryOrderFilter === SortingListingType.firstAvailabilityAsc) {
          setOrderValue(SortingListingType.firstAvailabilityAsc, searchResponse);
        }
      }
    };

    doNewSearch();

    return () => {
      shouldIgnore = true;
    };
  }, [
    params,
    service,
    isServerListing,
    setOrderLoading,
    setOrderValue,
    location,
    isUserGeolocationActive,
    isWelfare,
    setLoadingState,
    serviceSearchParam,
    locationQueryParam,
  ]);

  const listingInfoRef = useRef<HTMLDivElement>(null);
  const scrollToListingStart = () => {
    const offsetTop = 150;
    const targetPosition = listingInfoRef.current?.getBoundingClientRect().top ?? 0;
    const scrollPosition = window.scrollY + targetPosition - offsetTop;

    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth',
    });
  };

  const setLocationParams = ({ place, coordinates }: { place: string; coordinates: [number, number] }) => {
    if (userGeolocationActive) return;
    const newParams = getGeoLocationParams(params, { place, coordinates: { lat: coordinates[0], lng: coordinates[1] } });
    window.history.pushState(null, '', `?${newParams.toString()}`);
  };

  useEffect(() => {
    if (!showSearchNearYouCTA) window.scrollTo(0, 0);
  }, [listingInfo.results, showSearchNearYouCTA]);

  useEffect(() => {
    resetOrderValues();
  }, [locationInfo, serviceSearchParam, resetOrderValues, locationQueryParam]);

  return (
    <>
      {isShowingSearchNearYou && (
        <div className="flex items-center justify-center">
          {isSearchNearYouLoading ? (
            <ELLoader />
          ) : (
            <ELButton
              label={'Cerca vicino a te'}
              variant="filled"
              color="secondary"
              onClick={searchNearYou}
              leadingIcon={<MapPinIcon />}
              size="large"
            />
          )}
        </div>
      )}

      {loadingState && (
        <div className="my-24 flex flex-col items-center gap-4">
          <Image src={`${DAVINCI_CDN}/inclinic/images/waiting.png`} width={600} height={600} alt="waiting" className="max-w-[300px]" />
          <div className="flex flex-col items-center justify-center gap-y-4">
            <h3 className="text-center text-lg font-bold">Ricerca in corso...</h3>
            <ELLoader />
          </div>
        </div>
      )}
      <div className={`${loadingState ? 'hidden' : 'visible relative'}`}>
        {!isDesktopAndBigger && showSearchSection && (
          <Container>
            <Searchbar idPrefix={'listing'} className="py-3" asPlaceholder searchOnOptionClick openModalOnMobile />
          </Container>
        )}
        {isWelfare && <BannerWelfare />}
        <ListingFilters filterLight={filterLight} showMap={() => setShowMap(true)} />
        <Container>
          <div ref={listingInfoRef} className="grid grid-cols-1 lg:grid-cols-[4fr_2fr] lg:gap-x-12">
            <div className="flex flex-col">
              <div className="flex flex-col gap-2 py-4 md:py-6">
                <p className="text-sm font-normal text-neutral-lighter">{listingInfo?.title ?? ''}</p>

                {isServerListing && searchResultsServerSide && (
                  <p className="text-sm font-normal text-neutral-lighter">
                    Prezzi da {formatPrice({ amount: searchResultsServerSide.facets_stats?.['specializations.price']?.min })}
                    {serverDateFormatted && (
                      <>
                        &nbsp;• Aggiornato il&nbsp;
                        <time suppressHydrationWarning dateTime={searchResultsServerSide.date}>
                          {serverDateFormatted}
                        </time>
                      </>
                    )}
                  </p>
                )}
              </div>
              <p className="mb-2 text-xs font-semibold text-neutral-darker lg:text-sm">Ordina i risultati</p>
              {!!listingInfo?.results?.length && <ListingOrder loadingOrderValues={orderValuesLoading} />}

              {/* listing body */}
              {!!listingInfo?.results?.length && (
                <div className="grid grid-rows-1">
                  <div className="grid grid-cols-1 items-start gap-y-4">
                    {listingInfo.results.map(item => {
                      return <CardV3 key={item.objectID} item={item} showDistance={hasLocation} />;
                    })}
                  </div>
                  <div className={paginationPadding === 'lg' ? `pt-48` : 'pt-10'}>
                    <Paginator totalPages={listingInfo?.pages} />
                  </div>
                </div>
              )}
            </div>
            {/* map */}
            {(!!makeMarkersList(listingInfo?.results ?? [])?.length || isUserGeolocationActive) && !isDesktopAndBigger ? (
              <>
                {/* mobile/tablet */}
                {hasMapLoaded && (
                  <div className={clsx('block', !isShowingMap && 'hidden')}>
                    <div className="absolute inset-0 -left-2 -top-2 z-[90] h-full">
                      <div className="h-screen w-full">
                        <MapStaticToInteractive
                          items={listingInfo.results ?? []}
                          //setUserGeolocation={setLocationParams}
                          //isUserGeolocationActive={!!userGeolocationActive}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* desktop */}
                <div className="lg:relative lg:flex lg:min-h-full lg:w-full">
                  <div className="sticky top-0.5 h-screen w-full">
                    <div className="h-full w-full">
                      {shouldLoadDesktopMap && (
                        <MapStaticToInteractive
                          items={listingInfo.results ?? []}
                          //setUserGeolocation={setLocationParams}
                          //isUserGeolocationActive={!!userGeolocationActive}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </Container>

        <div className={clsx('xl:hidden', !!makeMarkersList(listingInfo?.results ?? [])?.length ? 'relative' : 'hidden')}>
          {isShowingMap && (
            <div className="fixed bottom-10 z-[1000] flex w-full justify-center">
              <ELButton
                aria-label={'Mostra lista risultati'}
                label={'Lista'}
                variant="filled"
                color="secondary"
                onClick={() => {
                  scrollToListingStart();
                  setShowMap(!isShowingMap);
                }}
                leadingIcon={<Bars4Icon />}
                size="medium"
                float
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Listing;
